import React, { useEffect, useRef, useState } from 'react';
import { Timer, Lock, CheckCircle2, RotateCcw } from 'lucide-react';

/**
 * RPE → emoji + tone color.
 */
const rpeFace = (rpe) => {
  if (rpe == null) return null;
  if (rpe <= 4) return { face: '🙂', cls: 'text-green-300' };
  if (rpe <= 6) return { face: '😐', cls: 'text-yellow-300' };
  if (rpe <= 8) return { face: '😣', cls: 'text-orange-300' };
  return { face: '🥵', cls: 'text-red-300' };
};

const fmtRest = (s) => {
  const n = parseInt(s, 10);
  if (!n || isNaN(n)) return '—';
  if (n < 60) return `${n} s`;
  if (n % 60 === 0) return `${n / 60} min`;
  return `${(n / 60).toFixed(1)} min`;
};

// Column order (user-tested): Serie | Reps | KG | RPE | Descanso
// minmax(0,…) lets every column shrink below its content width so the active
// row's inputs never force horizontal overflow / left-edge clipping on phones.
const GRID = '44px minmax(0,1fr) minmax(0,1fr) minmax(0,0.85fr) minmax(0,1.05fr)';

// Sane input caps to stop fat-finger entries (e.g. 200 reps, 1000 kg). Values
// above these are almost always a misclick; a trainer can still coach-edit.
const MAX_REPS = 100;
const MAX_WEIGHT = 500; // kg

/**
 * <SetTable />
 *
 * Columns: Serie │ Reps │ KG │ RPE │ Descanso (reps-first — users think in
 * "reps with weight"). Active row has strong focus + integrated action button.
 * Rest pills include 60/90/120/180/240 s; tapping RPE during rest auto-updates
 * the suggested rest (handled by parent via onChangeSetData).
 */
const SetTable = ({
  targetSets,
  targetReps,
  targetWeight,
  actualSets = [],
  setData,
  onChangeSetData,
  isResting = false,
  onComplete,
  completeLabel = 'Completar serie',
  completeDisabled = false,
  // Rest auto-adjusted hint (briefly shown on the rest pills)
  restAutoHint = false,
  // One-tap "repeat last session" prefill for the ACTIVE set: { reps, weight }
  // taken from the matching set of the client's previous session (or null).
  repeatLastSet = null,
  onRepeatLast = null,
  // Coach Mode — when true, completed (done) rows become editable so the
  // trainer can correct a wrong client input without rolling the workout
  // back. Edits are persisted via `onEditDoneSet(setIndex, patch)`.
  isCoachMode = false,
  onEditDoneSet = null,
  // Identifier that must change when the parent navigates to a different
  // exercise. Used to force-remount the uncontrolled edit inputs so they
  // re-pick up the new exercise's actual_sets values (defaultValue only
  // applies on mount; without this React reuses the same DOM nodes and
  // displays the previous exercise's data).
  exerciseKey = '',
}) => {
  const total = Math.max(targetSets || 0, actualSets.length + 1);
  const currentIndex = actualSets.length;

  // Flash animation on the just-completed row
  const [flashIndex, setFlashIndex] = useState(null);
  const prevLenRef = useRef(actualSets.length);
  useEffect(() => {
    if (actualSets.length > prevLenRef.current) {
      const idx = actualSets.length - 1;
      setFlashIndex(idx);
      const t = setTimeout(() => setFlashIndex(null), 900);
      prevLenRef.current = actualSets.length;
      return () => clearTimeout(t);
    }
    prevLenRef.current = actualSets.length;
  }, [actualSets.length]);

  return (
    <div
      className="rounded-xl border border-white/10 bg-slate-950/40 overflow-visible mb-4"
      data-testid="set-table"
    >
      {/* Header row — REPS FIRST */}
      <div
        className="grid items-center px-3 py-2 text-[11px] uppercase tracking-widest font-semibold text-slate-400 bg-white/[0.02] border-b border-white/5 rounded-t-xl"
        style={{ gridTemplateColumns: GRID }}
      >
        <span>Serie</span>
        <span className="text-right pr-3">Reps</span>
        <span className="text-right pr-3">KG</span>
        <span className="text-right pr-3">RPE</span>
        <span className="text-right pr-1">Descanso</span>
      </div>

      <div className="divide-y divide-white/5">
        {Array.from({ length: total }, (_, i) => {
          const status = i < actualSets.length ? 'done' : i === currentIndex ? 'current' : 'future';

          // ── DONE ROW ────────────────────────────────────────────────────
          if (status === 'done') {
            const s = actualSets[i];
            const face = rpeFace(s?.rpe);
            const isFlashing = flashIndex === i;

            // Coach Mode: render editable inputs so the trainer can correct
            // a wrong client log without rolling the set back. Persists
            // on blur or Enter via onEditDoneSet(setIndex, patch).
            if (isCoachMode && onEditDoneSet) {
              const commit = (patch) => {
                // Only persist if at least one value actually changed
                const next = { ...s, ...patch };
                if (
                  Number(next.reps) === Number(s?.reps) &&
                  Number(next.weight) === Number(s?.weight) &&
                  (next.rpe ?? null) === (s?.rpe ?? null)
                ) return;
                onEditDoneSet(i, patch);
              };
              return (
                <div
                  key={`edit-${exerciseKey}-${i}`}
                  className="grid items-center px-3 py-2 text-sm bg-amber-500/[0.05] border-l-2 border-amber-400/60"
                  style={{ gridTemplateColumns: GRID }}
                  data-testid={`set-row-done-edit-${i}`}
                  title="Coach Mode — corregir valores"
                >
                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-semibold bg-amber-500/30 text-amber-100 ring-1 ring-amber-400/40">
                    ✏️
                  </span>
                  {/* Reps — editable */}
                  <div className="flex justify-end pr-2">
                    <input
                      type="number"
                      inputMode="numeric"
                      min="0"
                      max={MAX_REPS}
                      defaultValue={s?.reps ?? 0}
                      onBlur={(e) => commit({ reps: Math.max(0, Math.min(parseInt(e.target.value || '0', 10) || 0, MAX_REPS)) })}
                      onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                      className="w-20 text-right bg-slate-900/80 border border-amber-400/40 rounded px-2 py-1 text-amber-100 tabular-nums focus:outline-none focus:ring-1 focus:ring-amber-400"
                      data-testid={`coach-edit-reps-${i}`}
                      aria-label={`Edit reps for completed set ${i + 1}`}
                    />
                  </div>
                  {/* Weight — editable */}
                  <div className="flex justify-end pr-2">
                    <input
                      type="text"
                      inputMode="decimal"
                      step="0.5"
                      min="0"
                      defaultValue={s?.weight ?? 0}
                      onBlur={(e) => commit({ weight: Math.max(0, Math.min(parseFloat((e.target.value || '0').replace(',', '.')) || 0, MAX_WEIGHT)) })}
                      onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                      className="w-24 text-right bg-slate-900/80 border border-amber-400/40 rounded px-2 py-1 text-amber-100 tabular-nums focus:outline-none focus:ring-1 focus:ring-amber-400"
                      data-testid={`coach-edit-weight-${i}`}
                      aria-label={`Edit weight for completed set ${i + 1}`}
                    />
                  </div>
                  {/* RPE — editable (0–10, blank allowed) */}
                  <div className="flex justify-end pr-3">
                    <input
                      type="number"
                      inputMode="numeric"
                      min="1"
                      max="10"
                      defaultValue={s?.rpe ?? ''}
                      onBlur={(e) => {
                        const v = e.target.value === '' ? null : parseInt(e.target.value, 10);
                        commit({ rpe: v });
                      }}
                      onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                      placeholder="—"
                      className="w-14 text-right bg-slate-900/80 border border-amber-400/40 rounded px-1 py-1 text-amber-100 tabular-nums focus:outline-none focus:ring-1 focus:ring-amber-400 placeholder:text-slate-600"
                      data-testid={`coach-edit-rpe-${i}`}
                      aria-label={`Edit RPE for completed set ${i + 1}`}
                    />
                  </div>
                  <span className="text-right pr-1 text-slate-400 tabular-nums">{fmtRest(s?.rest_time)}</span>
                </div>
              );
            }

            return (
              <div
                key={i}
                className={`grid items-center px-3 py-2 text-sm transition-all duration-500 ${
                  isFlashing
                    ? 'bg-green-500/15 border-l-2 border-green-400 opacity-100'
                    : 'opacity-55 hover:opacity-90 border-l-2 border-transparent'
                }`}
                style={{ gridTemplateColumns: GRID }}
                data-testid={`set-row-done-${i}`}
              >
                <span
                  className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-semibold transition-all ${
                    isFlashing
                      ? 'bg-green-500 text-white scale-110 shadow-md shadow-green-500/40'
                      : 'bg-slate-800 text-slate-300'
                  }`}
                >
                  {isFlashing ? <CheckCircle2 className="w-4 h-4" /> : '✓'}
                </span>
                <span className="text-right pr-3 text-slate-200 tabular-nums">{s?.reps ?? 0}</span>
                <span className="text-right pr-3 text-slate-200 tabular-nums">{s?.weight ?? 0}</span>
                <span className="text-right pr-3 tabular-nums">
                  {face ? (
                    <span className={`inline-flex items-center gap-1 ${face.cls}`}>
                      <span>{face.face}</span>
                      <span>{s.rpe}</span>
                    </span>
                  ) : (
                    <span className="text-slate-500">—</span>
                  )}
                </span>
                <span className="text-right pr-1 text-slate-400 tabular-nums">{fmtRest(s?.rest_time)}</span>
              </div>
            );
          }

          // ── CURRENT ROW ─────────────────────────────────────────────────
          if (status === 'current') {
            const setsLabel = `Serie ${i + 1} de ${targetSets || total}`;
            const isFinalRow = i === total - 1 && i + 1 >= (targetSets || total);
            return (
              <div
                key={i}
                className={`relative origin-center transition-all duration-300 ${
                  isResting
                    ? 'bg-amber-500/[0.07] ring-1 ring-amber-400/40 scale-[1.01]'
                    : 'bg-blue-500/[0.13] ring-2 ring-blue-400/50 shadow-[0_0_24px_-4px_rgba(59,130,246,0.45)] scale-[1.02]'
                } rounded-lg my-1.5 mx-1`}
                data-testid={`set-row-current-${i}`}
              >
                <div
                  className={`absolute left-0 top-0 bottom-0 w-1 rounded-l-lg ${
                    isResting ? 'bg-amber-400' : 'bg-blue-400'
                  }`}
                />

                <div className="px-4 pt-2.5 pb-1 text-[10px] uppercase tracking-widest font-bold flex items-center gap-2">
                  <span className={isResting ? 'text-amber-300' : 'text-blue-300'}>
                    {isResting ? '⏱ Descansando · próxima:' : '▶ Ahora'}
                  </span>
                  <span className="text-slate-400">{setsLabel}</span>
                </div>

                {/* Inputs row — REPS FIRST, then KG */}
                <div
                  className="grid items-center px-4 pb-2 text-sm"
                  style={{ gridTemplateColumns: GRID }}
                >
                  <span
                    className={`inline-flex items-center justify-center w-10 h-10 rounded-full text-base font-bold ${
                      isResting
                        ? 'bg-amber-500/25 text-amber-100 ring-1 ring-amber-300/40'
                        : 'bg-blue-500 text-white shadow-md shadow-blue-500/30 ring-2 ring-blue-300/50'
                    }`}
                  >
                    {i + 1}
                  </span>

                  {/* Reps — FIRST — empty by default, target as placeholder */}
                  <div className="flex justify-end pr-2 min-w-0">
                    <input
                      type="number"
                      inputMode="numeric"
                      min="0"
                      max={MAX_REPS}
                      value={setData.reps}
                      onChange={(e) => {
                        const raw = e.target.value;
                        const clamped = raw === '' ? '' : String(Math.max(0, Math.min(parseInt(raw, 10) || 0, MAX_REPS)));
                        onChangeSetData({ ...setData, reps: clamped });
                      }}
                      disabled={isResting}
                      placeholder={targetReps ? String(targetReps) : '—'}
                      className="w-full min-w-0 max-w-[76px] text-right bg-slate-900/80 border border-blue-400/40 rounded-md px-1.5 py-2 text-white tabular-nums text-lg font-bold focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 placeholder:text-slate-600 placeholder:font-normal disabled:opacity-50"
                      data-testid="set-input-reps"
                      aria-label={`Reps serie ${i + 1}`}
                      autoFocus={!isResting && !setData.reps}
                    />
                  </div>

                  {/* KG — SECOND — pre-filled (first set: target; next: previous set) */}
                  <div className="flex justify-end pr-2 min-w-0">
                    <input
                      type="text"
                      inputMode="decimal"
                      step="0.5"
                      min="0"
                      value={setData.weight}
                      onChange={(e) => {
                        let v = e.target.value.replace(',', '.').replace(/[^0-9.]/g, '');
                        if (v !== '' && parseFloat(v) > MAX_WEIGHT) v = String(MAX_WEIGHT);
                        onChangeSetData({ ...setData, weight: v });
                      }}
                      disabled={isResting}
                      placeholder={targetWeight ? String(targetWeight) : '0'}
                      className="w-full min-w-0 max-w-[84px] text-right bg-slate-900/80 border border-blue-400/40 rounded-md px-1.5 py-2 text-white tabular-nums text-lg font-bold focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 placeholder:text-slate-600 placeholder:font-normal disabled:opacity-50"
                      data-testid="set-input-weight"
                      aria-label={`Peso serie ${i + 1}`}
                    />
                  </div>

                  {/* RPE */}
                  <div className="flex justify-end pr-2 min-w-0">
                    {setData.rpe ? (
                      (() => {
                        const f = rpeFace(parseInt(setData.rpe, 10));
                        return (
                          <span className={`inline-flex items-center gap-1 ${f?.cls || 'text-slate-300'} font-medium`}>
                            <span className="text-base">{f?.face}</span>
                            <span>{setData.rpe}</span>
                          </span>
                        );
                      })()
                    ) : (
                      <span className="inline-flex items-center gap-0.5 text-slate-500 text-[10px] min-w-0">
                        <Lock className="w-3 h-3 flex-shrink-0" />
                        <span className="italic truncate">tras descanso</span>
                      </span>
                    )}
                  </div>

                  {/* Descanso — read-only target (no pre-set selection).
                      Manual override is available during rest in the elapsed
                      timer panel. RPE auto-suggests this value. */}
                  <div className="flex justify-end items-center gap-1 pr-1 min-w-0">
                    <Timer className={`w-3 h-3 flex-shrink-0 ${restAutoHint ? 'text-amber-300' : 'text-slate-500'}`} />
                    <span
                      className={`text-[11px] tabular-nums truncate ${
                        restAutoHint ? 'text-amber-200 font-semibold' : 'text-slate-400'
                      }`}
                      data-testid="set-rest-target"
                    >
                      {fmtRest(setData.rest_time)}
                    </span>
                  </div>
                </div>

                {/* One-tap "repeat last session" prefill for this set */}
                {!isResting && repeatLastSet && onRepeatLast && (
                  <div className="px-4 pb-2">
                    <button
                      type="button"
                      onClick={onRepeatLast}
                      data-testid="repeat-last-set-btn"
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium text-slate-300 bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] hover:text-white hover:border-blue-400/40 active:scale-95 transition"
                    >
                      <RotateCcw className="w-3.5 h-3.5" />
                      <span>Repetir última</span>
                      <span className="tabular-nums text-slate-400">
                        · {repeatLastSet.reps}{(repeatLastSet.weight != null && repeatLastSet.weight !== '' && Number(repeatLastSet.weight) > 0) ? ` × ${repeatLastSet.weight} kg` : ' reps'}
                      </span>
                    </button>
                  </div>
                )}

                {/* Auto-rest hint — RPE shifted the recommended target.
                    Elapsed timer keeps running; only the target updates. */}
                {restAutoHint && !isResting && (
                  <div className="px-4 pb-1 text-[10px] text-amber-300/90 italic flex items-center gap-1">
                    <span>⚡</span>
                    <span>Objetivo de descanso ajustado según esfuerzo</span>
                  </div>
                )}

                {/* Integrated action button */}
                {onComplete && (
                  <div className="px-3 pb-3 pt-2.5 mt-1 border-t border-white/[0.06]">
                    <button
                      type="button"
                      onClick={onComplete}
                      disabled={completeDisabled || isResting}
                      className={`w-full py-3 rounded-lg text-base font-bold transition-all select-none ${
                        completeDisabled || isResting
                          ? 'bg-slate-800/50 text-slate-500 cursor-not-allowed'
                          : 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-400 hover:to-blue-500 text-white shadow-lg shadow-blue-500/40 active:scale-[0.99]'
                      }`}
                      style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
                      data-testid="complete-set-btn"
                    >
                      {isResting
                        ? 'Descansando…'
                        : `${completeLabel} ${i + 1}${isFinalRow ? ' · finalizar ejercicio' : ''} ✓`}
                    </button>
                  </div>
                )}
              </div>
            );
          }

          // ── FUTURE ROW — reps first, kg second ─────────────────────────
          return (
            <div
              key={i}
              className="grid items-center px-3 py-2 text-sm text-slate-600"
              style={{ gridTemplateColumns: GRID }}
              data-testid={`set-row-future-${i}`}
            >
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-slate-900/60 border border-white/5 text-slate-600 text-xs">
                {i + 1}
              </span>
              <span className="text-right pr-3 tabular-nums">{targetReps || '—'}</span>
              <span className="text-right pr-3 tabular-nums">{targetWeight || '—'}</span>
              <span className="text-right pr-3 tabular-nums">—</span>
              <span className="text-right pr-1 tabular-nums">{fmtRest(setData.rest_time)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SetTable;
